// The Text button (CRM-TEXT-2026-10-16). Matt: "We need a way to click from the CRM and send a text to any of the
// people that come in on Formspree."
//
// The CRM side only: which leads get the button, the three editable templates, the thread preview, and a sender that
// sends ONCE per click. Every rule that decides whether a text may go -- STOP list, a textable number, 8am-8pm in the
// lead's local time (area code first, then state) unless "send anyway", 10 a minute, a repeated text not re-sent -- is
// enforced by the recruiter-agent Worker's POST /staff-text, behind functions/api/staff-text.js. Nothing here can
// loosen it; the UI only explains it.

/** How a staff-typed text (agent_messages.role = 'human') is labelled in a thread. One place, so it reads the same everywhere. */
export const STAFF_SENDER_LABEL = 'sent by Matt';

/** public.settings key that holds Matt's edited templates (a JSON array of three { key, label, body }). */
export const SMS_TEMPLATES_KEY = 'sms_templates';
export const SMS_MAX = 1600; // the Worker's STAFF_MSG_MAX

// {first} is the lead's first name; with no name on file the greeting drops it ("Hi, this is Matt...").
export const DEFAULT_SMS_TEMPLATES = [
  { key: 'first_touch', label: 'First touch',
    body: "Hi {first}, this is Matt at ProTherapy Staffing. Thanks for reaching out. Are you looking for a travel or local assignment right now? Reply STOP to opt out." },
  { key: 'follow_up', label: 'Follow-up',
    body: "Hi {first}, Matt at ProTherapy again, following up on my last text. What discipline and states are you looking at? Happy to send what's open." },
  { key: 'still_looking', label: 'Still looking?',
    body: "Hi {first}, are you still looking for your next assignment? Reply here and I'll send you what's open this week." },
];

/** The stored value (a JSON string, or nothing) -> always exactly three usable templates; a bad slot falls back to its default. */
export function parseTemplates(value) {
  let list = null;
  try { list = typeof value === 'string' ? JSON.parse(value) : value; } catch (_) { list = null; }
  return DEFAULT_SMS_TEMPLATES.map((d, i) => {
    const t = Array.isArray(list) ? list[i] : null;
    const body = t && typeof t.body === 'string' ? t.body.trim().slice(0, SMS_MAX) : '';
    const label = t && typeof t.label === 'string' && t.label.trim() ? t.label.trim().slice(0, 40) : d.label;
    return { key: d.key, label, body: body || d.body };
  });
}

/** Three templates -> the string saved under SMS_TEMPLATES_KEY. */
export function serializeTemplates(list) {
  return JSON.stringify(parseTemplates(list));
}

export function firstName(lead) {
  const n = String((lead && lead.name) || '').trim().split(/\s+/)[0] || '';
  return /^[A-Za-z][A-Za-z'’-]{0,29}$/.test(n) ? n.charAt(0).toUpperCase() + n.slice(1) : '';
}

/** Fill {first}. No first name -> "Hi {first}," becomes "Hi,". */
export function fillTemplate(body, lead) {
  const first = firstName(lead);
  const s = String(body || '');
  return first ? s.replace(/\{first\}/g, first) : s.replace(/\s*\{first\}/g, '');
}

/** The last `n` messages of a thread, oldest first, as { who, body, at } (who = them | agent | STAFF_SENDER_LABEL). */
export function threadPreview(messages, n = 3) {
  const rows = (Array.isArray(messages) ? messages : []).filter((m) => m && String(m.body || '').trim());
  return rows.slice(-n).map((m) => ({
    who: m.role === 'candidate' ? 'them' : m.role === 'human' ? STAFF_SENDER_LABEL : 'agent',
    body: String(m.body).trim(),
    at: m.created_at || null,
    failed: m.status === 'failed' || m.status === 'undelivered',
    // NIGHT-DIRECTSTAFF-2026-10-17 §5: the delivery line under a staff text reads these (lib/appPolish.js deliveryLine)
    status: m.status || null, error_code: m.error_code == null ? null : m.error_code, error_message: m.error_message || null,
  }));
}

/**
 * Whether a lead's Text button is live. EVERY lead row gets the button, whatever its source or form; it is greyed,
 * with the reason on hover, only when there is nothing to send to or the lead replied STOP. The Worker re-checks both.
 */
export function textButtonState(lead) {
  if (!lead) return { enabled: false, reason: 'No lead.' };
  if (lead.sms_opt_out === true) return { enabled: false, reason: 'This person replied STOP. They cannot be texted.' };
  const d = String(lead.phone || '').replace(/\D/g, '');
  const ten = d.length === 11 && d[0] === '1' ? d.slice(1) : d;
  if (ten.length !== 10 || ten[0] === '0' || ten[0] === '1') return { enabled: false, reason: 'No textable US number on file.' };
  return { enabled: true, reason: 'Text this lead from the business line' };
}

/**
 * One send per click. While a send is in flight every further call resolves to { skipped: true } without calling
 * `post` -- a double click, or a second tap on a slow network, cannot send twice. (The Worker also refuses the same
 * text to the same thread within a minute, for the click that arrives after the first finished.)
 */
export function createSingleFlightSender(post) {
  let inFlight = false;
  return async function send(payload) {
    if (inFlight) return { skipped: true };
    inFlight = true;
    try { return await post(payload); } finally { inFlight = false; }
  };
}

export const STAFF_TEXT_ERRORS = {
  invalid_number: 'This lead has no textable US mobile number on file. Nothing was sent.',
  opted_out: 'This person replied STOP. No message was sent.',
  quiet_hours: "It is outside 8am-8pm in this person's local time. Tick \"Send anyway\" to send now.",
  rate_limited: 'Ten texts a minute is the limit. Try again shortly.',
  lead_not_found: 'That lead no longer exists.',
  unauthorized: 'Not signed in, or your session expired.',
  forbidden: 'Blocked: this request did not come from the CRM.',
  send_failed: 'Twilio rejected the message. Nothing was delivered.',
  store_unavailable: 'The agent database is unreachable. Nothing was sent; try again shortly.',
  upstream: 'Could not reach the agent worker.',
};
